import React, { useMemo } from "react";
import { Field } from "react-final-form";
import { FormHelperText, ToggleButton, ToggleButtonGroup } from "@mui/material";
import RffInputWrapper from "../shared/RffInputWrapper";
import normalizeOptions from "../../../utils/finalForm/normalizeOptions";

/**
 * RffSelectToggleField — a single-select rendered as a row of pills rather than
 * a dropdown. Use it for short option lists the reader benefits from seeing all
 * at once (product type, citizenship, FICO band); keep RffSelectField for long
 * lists.
 *
 * Accepts options as plain strings or `{ value, label }`.
 */
const RffSelectToggleField = ({
  name,
  label,
  options = [],
  required = false,
  helperText,
  row = false,
  suppressGrid,
  size,
  disabled,
  onChange,
  alphabetize = false,
}) => {
  const resolvedOptions = useMemo(() => {
    const normalized = normalizeOptions(options);
    if (!alphabetize) return normalized;
    return [...normalized].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [options, alphabetize]);

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
      row={row}
    >
      <Field
        name={name}
        validate={(v) =>
          required && (v == null || v === "") ? "This field is required" : undefined
        }
        render={({ input, meta }) => (
          <>
            <ToggleButtonGroup
              exclusive
              color="primary"
              size="small"
              disabled={disabled}
              value={input.value === "" ? null : input.value}
              onChange={(_, next) => {
                // MUI hands back null when the active pill is clicked again;
                // treat that as clearing the answer rather than ignoring it,
                // except when the field is required.
                if (next === null && required) return;
                input.onChange(next === null ? undefined : next);
                if (onChange) onChange(next);
              }}
              // Same button metrics as RffBooleanToggleField so a select toggle
              // and a Yes/No toggle read as one control type. `flexWrap` is the
              // one addition: this field's option lists can be long enough to
              // overflow a row, which Yes/No never is.
              sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { minWidth: 72 } }}
            >
              {resolvedOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {meta.touched && meta.error && (
              <FormHelperText error>{meta.error}</FormHelperText>
            )}
          </>
        )}
      />
    </RffInputWrapper>
  );
};

export default RffSelectToggleField;
